import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { movies, categories } from '@/mocks/movies';
import MovieCard from '@/pages/home/components/MovieCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'default' | 'rating' | 'year'>('default');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const categoryInfo = useMemo(() => {
    return categories.find((c) => c.slug === slug) || { name: '全部', slug: 'all' };
  }, [slug]);

  const filteredMovies = useMemo(() => {
    let result = movies;
    if (slug && slug !== 'all') {
      if (slug === 'hot') {
        result = movies.filter((m) => m.isHot);
      } else {
        result = movies.filter((m) => m.categorySlug === slug);
      }
    }
    if (sortBy === 'rating') {
      return [...result].sort((a, b) => b.rating - a.rating);
    }
    if (sortBy === 'year') {
      return [...result].sort((a, b) => b.year - a.year);
    }
    return result;
  }, [slug, sortBy]);

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://taohua-cinema.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryInfo.name,
        item: `https://taohua-cinema.com/category/${slug}`,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-4 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <span>/</span>
              <span className="text-foreground-700 font-medium">{categoryInfo.name}</span>
            </nav>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {categoryInfo.name}
              </h1>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400 whitespace-nowrap">排序：</span>
                {[
                  { value: 'default', label: '默认' },
                  { value: 'rating', label: '评分最高' },
                  { value: 'year', label: '最新上映' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value as typeof sortBy)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                      sortBy === opt.value
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="py-20 text-center">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-movie-2-line text-3xl text-foreground-300"></i>
                </div>
                <p className="mt-4 text-foreground-400">该分类暂无内容</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}